/**
 * Closed-list country / state options for MANUAL addresses.
 *
 * A manual address bypasses the geocoder, so its country and state are typed by a
 * human. A spelling error there silently breaks route optimization, which matches a
 * manual address to its per-region routing graph by `country` (e.g. US) and `state`
 * (e.g. Texas ⇒ tx). To make that impossible, the manual-address editor offers these
 * values as strict closed-list autocompletes (no free text).
 *
 * The state values mirror the keys understood by the backend
 * `FleetService::normalizeStateAbbreviation` (full names), and the supported-country
 * detection mirrors `FleetService::normalizeCountryCode` (US / CA / AU / GB).
 */

// Full English country names. Kept broad so any household location can be entered;
// only US / CA / AU / GB additionally surface a state/province list (the others route
// by country alone, so a free state would not help and could only hurt).
export const COUNTRIES: string[] = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Argentina', 'Austria',
  'Belgium', 'Brazil', 'Bulgaria', 'Chile', 'Colombia', 'Costa Rica', 'Croatia',
  'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Georgia', 'Germany',
  'Greece', 'Hungary', 'Iceland', 'India', 'Ireland', 'Israel', 'Italy', 'Japan',
  'Latvia', 'Lithuania', 'Luxembourg', 'Mexico', 'Morocco', 'Netherlands',
  'New Zealand', 'Norway', 'Panama', 'Peru', 'Poland', 'Portugal', 'Romania',
  'Russia', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa',
  'South Korea', 'Spain', 'Sweden', 'Switzerland', 'Thailand', 'Turkey', 'Ukraine',
  'Uruguay', 'Venezuela',
];

// US states + DC — values match FleetService::normalizeStateAbbreviation keys.
export const US_STATES: string[] = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine',
  'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri',
  'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee',
  'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming',
];

// UK constituent nations — Photon stores the nation in address.state and the router
// has a per-nation graph (england / scotland / wales / northern-ireland).
export const UK_NATIONS: string[] = ['England', 'Scotland', 'Wales', 'Northern Ireland'];

// Canadian provinces/territories — the router routes Canada as one country graph, but
// surfacing the province keeps the saved address human-readable and unambiguous.
export const CA_PROVINCES: string[] = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick',
  'Newfoundland and Labrador', 'Nova Scotia', 'Ontario', 'Prince Edward Island',
  'Quebec', 'Saskatchewan', 'Northwest Territories', 'Nunavut', 'Yukon',
];

// Australian states/territories — routed as one country graph (see CA note).
export const AU_STATES: string[] = [
  'New South Wales', 'Victoria', 'Queensland', 'Western Australia', 'South Australia',
  'Tasmania', 'Australian Capital Territory', 'Northern Territory',
];

/**
 * Normalize a free-form country string to the two-letter code used for routing —
 * a frontend mirror of FleetService::normalizeCountryCode. Returns '' when empty.
 */
export const normalizeCountryCode = (country?: string | null): string => {
  const c = (country ?? '').trim().toLowerCase();
  if (c === '') return '';
  const map: Record<string, string> = {
    us: 'US', usa: 'US', 'united states': 'US', 'united states of america': 'US',
    ca: 'CA', canada: 'CA',
    au: 'AU', australia: 'AU',
    gb: 'GB', uk: 'GB', 'united kingdom': 'GB', 'great britain': 'GB',
    il: 'IL', israel: 'IL', 'ישראל': 'IL', 'state of israel': 'IL',
  };
  return map[c] ?? c.toUpperCase();
};

/**
 * The closed-list state/province options for a country, or [] when the country routes
 * by country alone (no state list — the State field is hidden for those).
 */
export const statesForCountry = (country?: string | null): string[] => {
  switch (normalizeCountryCode(country)) {
    case 'US': return US_STATES;
    case 'GB': return UK_NATIONS;
    case 'CA': return CA_PROVINCES;
    case 'AU': return AU_STATES;
    default: return [];
  }
};

/** Whether a state is REQUIRED for this country (only US / UK route per-state). */
export const isUsOrUk = (country?: string | null): boolean => {
  const code = normalizeCountryCode(country);
  return code === 'US' || code === 'GB';
};
